import { Link } from "@tanstack/react-router";
import { ArrowRight, LineChart } from "lucide-react";

type Props = {
  /** Short context sentence tying this lesson to the published record. */
  context?: string;
  /** Keyword rich anchor text for the internal link. */
  anchor?: string;
};

/**
 * Internal link bridge: concept lessons into the public performance record.
 *
 * Purpose is twofold. It gives search engines a keyword relevant internal
 * link from high impression educational pages into /track-record, and it
 * gives the reader an evidence step after the theory. It discloses no
 * engine internals: only published, aggregated outcomes.
 */
export function TrackRecordCallout({
  context = "Theory is only half of the evaluation. The other half is what the method produced once it was executed under fixed rules.",
  anchor = "verified gold signals track record, 2024 to 2026",
}: Props) {
  return (
    <aside className="not-prose my-10 rounded-xl border border-emerald/30 bg-emerald-muted/20 p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-muted text-emerald">
          <LineChart className="h-5 w-5" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Evidence
          </div>
          <h3 className="mt-1 font-display text-lg font-bold">
            See the method measured, not described
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {context} 241 closed XAUUSD positions, profit factor 1.72, deepest
            decline 8.25R, month by month, with no selected screenshots.
          </p>
          <Link
            to="/track-record"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald hover:underline"
          >
            {anchor} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </aside>
  );
}
